import React, { useMemo } from "react";
import moment from "moment-timezone";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

function TimerList({
  currentPageData,
  timer,
  isTimerDataLoading,
  selectedUser,
  leastDate,
  highestDate,
  leastLastWeekDate,
  highestLastWeekDate,
  thisWeekRendered,
  WeekTotalDisplay,
  lastWeekRendered,
  getMomentDate,
  extremesByWeek,
  findLeastDate,
  findExtremeDate,
  TimerCard,
  resumeTimeTracking,
  appSettings,
  resumeLoading,
  customeTime,
  Modal,
  taskModalIsOpen,
  setTaskModalIsOpen,
  taskName,
  setTaskName,
  showingDropdownRef,
  setShowingDropdownOpen,
  showingDropdownOpen,
  totalResults,
  perPage,
  setPerPage,
  setSelectedPage,
  pageCount,
  ReactPaginate,
  selectedPage,
  handlePageChange,
  globalEditedId,
  setGlobalEditedId,
  refetchTimerData,
}) {
  const tz = global.config.getTimeZone();

  const weekBoundaryDateSet = useMemo(() => {
    const set = new Set();
    (extremesByWeek || []).forEach((extreme) => {
      if (extreme?.highestWeekDate) {
        set.add(moment.tz(extreme.highestWeekDate, tz).format("YYYY-MM-DD"));
      }
    });
    return set;
  }, [extremesByWeek, tz]);

  // Week buckets are compared against day keys ("YYYY-MM-DD"), which carry no
  // timezone, so the week must be the VIEWER's own week. Deriving it in the
  // tenant zone put entries in the wrong week for anyone whose zone differed.
  const weekRanges = useMemo(() => {
    const now = moment();
    const isMondayStart = appSettings?.weekStartDay === "Monday";

    const currentWeekStart = isMondayStart ? now.clone().startOf("isoWeek") : now.clone().startOf("week");
    const currentWeekEnd = currentWeekStart.clone().add(6, "days");

    const lastWeekStart = currentWeekStart.clone().subtract(7, "days");
    const lastWeekEnd = currentWeekEnd.clone().subtract(7, "days");

    return {
      currentWeekStart: currentWeekStart.format("YYYY-MM-DD"),
      currentWeekEnd: currentWeekEnd.format("YYYY-MM-DD"),
      lastWeekStart: lastWeekStart.format("YYYY-MM-DD"),
      lastWeekEnd: lastWeekEnd.format("YYYY-MM-DD"),
    };
  }, [appSettings?.weekStartDay]);

  // WeekTotal API expects strings that preserve timezone offset (NOT JS Date -> ISO Z).
  // The backend does: moment(startDate).startOf('day').utc(), so we must send offset-aware strings.
  const toZonedISO = (d, boundary) => {
    // `d` is a plain "YYYY-MM-DD" day key; interpret it in the reporting zone
    // to produce the offset-aware bounds the week-total API expects.
    const m = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)
      ? moment.tz(d, "YYYY-MM-DD", tz)
      : moment.tz(d, tz);
    const bounded = boundary === "start" ? m.startOf("day") : m.endOf("day");
    return bounded.format(); // e.g. 2026-01-15T00:00:00+05:30
  };

  // IMPORTANT: Don't mutate refs/values during render (StrictMode renders twice in dev).
  // Instead, compute the first index for each header deterministically.
  const firstThisWeekIndex = useMemo(() => {
    if (!Array.isArray(currentPageData) || currentPageData.length === 0) return -1;
    return currentPageData.findIndex((item) => {
      const day = typeof item?.[0] === "string" ? item[0] : null;
      return !!day && day >= weekRanges.currentWeekStart && day <= weekRanges.currentWeekEnd;
    });
  }, [currentPageData, tz, weekRanges.currentWeekStart, weekRanges.currentWeekEnd]);

  const firstLastWeekIndex = useMemo(() => {
    if (!Array.isArray(currentPageData) || currentPageData.length === 0) return -1;
    return currentPageData.findIndex((item) => {
      const day = typeof item?.[0] === "string" ? item[0] : null;
      return !!day && day >= weekRanges.lastWeekStart && day <= weekRanges.lastWeekEnd;
    });
  }, [currentPageData, tz, weekRanges.lastWeekStart, weekRanges.lastWeekEnd]);

  return (
    <>
      <style>{`
        @keyframes timerCardFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes timerShimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .timer-entry-row {
          animation: timerCardFadeIn 0.22s ease both;
          position: relative;
        }
        /* The fade-in animation makes each row its own stacking context, which traps
           any dropdown opened inside it below rows that come later in the DOM
           (they'd otherwise paint on top regardless of the dropdown's own z-index).
           Elevate whichever row currently has an open dropdown above its siblings. */
        .timer-entry-row:has(.custom-dropdown),
        .timer-entry-row:has(.popup),
        .timer-entry-row:has(.rs-picker-date-menu),
        .timer-entry-row:has(.rs-picker-menu),
        .timer-entry-row:has(.rs-picker-open),
        .timer-entry-row:has(.rs-picker-toggle-active) {
          z-index: 30;
          overflow: visible !important;
        }
        .timer-entry-row:has(.rs-picker-date-menu) .timetracker-inner,
        .timer-entry-row:has(.rs-picker-menu) .timetracker-inner,
        .timer-entry-row:has(.rs-picker-open) .timetracker-inner {
          overflow: visible !important;
          z-index: 31;
        }
        .timer-sk {
          background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
          background-size: 800px 100%;
          animation: timerShimmer 1.4s ease-in-out infinite;
          border-radius: 4px;
        }
      `}</style>
      {timer.length > 0 ? (
        <div className="timer-list-content">
        {currentPageData.map((item, index) => {
          const itemDay = moment.tz(item?.[0], "YYYY-MM-DD", tz).startOf("day");

          const shouldShowThisWeekHeader = index === firstThisWeekIndex && firstThisWeekIndex !== -1;
          const shouldShowLastWeekHeader = index === firstLastWeekIndex && firstLastWeekIndex !== -1;

          const itemDayStr = itemDay.format("YYYY-MM-DD");
          const isWeekBoundary = weekBoundaryDateSet.has(itemDayStr);

          const shouldShowWeekBoundaryHeader =
            isWeekBoundary &&
            // Don't show a "custom week" header for weeks already labeled as This Week / Last Week
            !(itemDayStr >= weekRanges.currentWeekStart && itemDayStr <= weekRanges.currentWeekEnd) &&
            !(itemDayStr >= weekRanges.lastWeekStart && itemDayStr <= weekRanges.lastWeekEnd);

          return (
            <div
              key={`${item?.[0]}-${index}`}
              className="timer-entry-row"
              style={{ animationDelay: `${index * 0.04}s` }}
            >
              {shouldShowThisWeekHeader && (
                <div className="total-time-tracked">
                  <div className="total-time-date-left">This Week</div>
                  <div className="total-time-date-right">
                    Week Total:{" "}
                    <WeekTotalDisplay
                      startDate={toZonedISO(weekRanges.currentWeekStart, "start")}
                      endDate={toZonedISO(weekRanges.currentWeekEnd, "end")}
                      selectedUser={selectedUser}
                    />
                  </div>
                </div>
              )}

              {shouldShowLastWeekHeader && (
                <div className="total-time-tracked">
                  <div className="total-time-date-left">Last Week</div>
                  <div className="total-time-date-right">
                    Week Total:{" "}
                    <WeekTotalDisplay
                      startDate={toZonedISO(weekRanges.lastWeekStart, "start")}
                      endDate={toZonedISO(weekRanges.lastWeekEnd, "end")}
                      selectedUser={selectedUser}
                    />
                  </div>
                </div>
              )}

              {shouldShowWeekBoundaryHeader && (
                <div className="total-time-tracked">
                  <div className="total-time-date-left">
                    {(() => {
                      const itemDate = moment.tz(item?.[0], "YYYY-MM-DD", tz).toDate();
                      const leastD = findLeastDate(itemDate, extremesByWeek);
                      const extremeD = findExtremeDate(itemDate, extremesByWeek);
                      return (
                        <>
                          {leastD && extremeD
                            ? `${moment(leastD).format("MMM D")} - ${moment(extremeD).format("MMM D")}`
                            : moment.tz(item?.[0], "YYYY-MM-DD", tz).format("MMM D")}
                          {moment.tz(item?.[0], "YYYY-MM-DD", tz).year() !== moment().year() && (
                            <span><span>,</span> {moment.tz(item?.[0], "YYYY-MM-DD", tz).year()}</span>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="total-time-date-right">
                    Week Total:{" "}
                    <WeekTotalDisplay
                      selectedUser={selectedUser}
                      startDate={toZonedISO(findLeastDate(moment.tz(item?.[0], "YYYY-MM-DD", tz).toDate(), extremesByWeek) || moment.tz(item?.[0], "YYYY-MM-DD", tz).startOf("isoWeek").toDate(), "start")}
                      endDate={toZonedISO(findExtremeDate(moment.tz(item?.[0], "YYYY-MM-DD", tz).toDate(), extremesByWeek) || moment.tz(item?.[0], "YYYY-MM-DD", tz).endOf("isoWeek").toDate(), "end")}
                    />
                  </div>
                </div>
              )}

              <TimerCard
                index={index}
                item={item}
                resumeTimeTracking={resumeTimeTracking}
                timeFormat={appSettings.timeFormat}
                resumeLoading={resumeLoading}
                customeTime={customeTime}
                selectedUser={selectedUser}
                globalEditedId={globalEditedId}
                setGlobalEditedId={setGlobalEditedId}
                refetchTimerData={refetchTimerData}
              />
            </div>
          );
        })}
        </div>
      ) : isTimerDataLoading === true ? (
        <>
          {/* Skeleton for week total header section - Top */}
          <div className="total-time-tracked">
            <div className="total-time-date-left">
              <div className="timer-sk" style={{ width: "120px", height: "18px" }} />
            </div>
            <div className="total-time-date-right">
              <div className="timer-sk" style={{ width: "90px", height: "18px" }} />
            </div>
          </div>

          {/* Skeleton for timer cards - First group */}
          {Array.from({ length: 3 }).map((_, index) => (
            <Card
              key={`skeleton-card-${index}`}
              className="text-center card-bg"
              style={{ marginBottom: "25px" }}
            >
              {/* Card Header Skeleton */}
              <Card.Header>
                <Row className="card-header-sec">
                  <Col className="card-header-date">
                    <div className="timer-sk" style={{ width: "120px", height: "27px" }} />
                  </Col>
                  <div className="separation-head"></div>
                  <Col className="card-timer">
                    <div className="timer-sk" style={{ width: "80px", height: "27px" }} />
                  </Col>
                </Row>
              </Card.Header>
              {/* Card Body Skeleton */}
              <Card.Body className="body-mg">
                <div className="timetracker-inner data-time-tracker overview-sec-timer">
                  <div className="merge-tracker">
                    <div className="overview-sec-timer-inner">
                      <div className="project-name">
                        <Col>
                          <div className="timer-sk" style={{ width: "200px", height: "20px" }} />
                        </Col>
                      </div>
                      <div className="project-sprint-outer col">
                        <div className="timer-sk" style={{ width: "150px", height: "20px" }} />
                      </div>
                    </div>
                    <div className="outer-right-sec-overview">
                      <div className="overview-sec-timer-inner-right">
                        <div className="tag-section-mobile-section">
                          <div className="tag-section">
                            <div className="timer-sk" style={{ width: "80px", height: "35px", borderRadius: "100px" }} />
                          </div>
                          <div className="billable-section">
                            <div className="timer-sk" style={{ width: "45px", height: "31px", borderRadius: "100px" }} />
                          </div>
                        </div>
                        <div className="overview-sec-timer-date-time">
                          <Col>
                            <div className="timer-sk" style={{ width: "140px", height: "20px" }} />
                          </Col>
                        </div>
                        <div className="overview-time-edit">
                          <Col>
                            <div className="overview-time-edit-inner">
                              <div className="timer-sk" style={{ width: "70px", height: "20px" }} />
                            </div>
                          </Col>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}

          {/* Skeleton for week total header section */}
          <div className="total-time-tracked">
            <div className="total-time-date-left">
              <div className="timer-sk" style={{ width: "120px", height: "18px" }} />
            </div>
            <div className="total-time-date-right">
              <div className="timer-sk" style={{ width: "90px", height: "18px" }} />
            </div>
          </div>

          {/* Skeleton for timer cards - Second group */}
          {Array.from({ length: 3 }).map((_, index) => (
            <Card
              key={`skeleton-card-${index + 3}`}
              className="text-center card-bg"
              style={{ marginBottom: "25px" }}
            >
              {/* Card Header Skeleton */}
              <Card.Header>
                <Row className="card-header-sec">
                  <Col className="card-header-date">
                    <div className="timer-sk" style={{ width: "120px", height: "27px" }} />
                  </Col>
                  <div className="separation-head"></div>
                  <Col className="card-timer">
                    <div className="timer-sk" style={{ width: "80px", height: "27px" }} />
                  </Col>
                </Row>
              </Card.Header>
              {/* Card Body Skeleton */}
              <Card.Body className="body-mg">
                <div className="timetracker-inner data-time-tracker overview-sec-timer">
                  <div className="merge-tracker">
                    <div className="overview-sec-timer-inner">
                      <div className="project-name">
                        <Col>
                          <div className="timer-sk" style={{ width: "200px", height: "20px" }} />
                        </Col>
                      </div>
                      <div className="project-sprint-outer col">
                        <div className="timer-sk" style={{ width: "150px", height: "20px" }} />
                      </div>
                    </div>
                    <div className="outer-right-sec-overview">
                      <div className="overview-sec-timer-inner-right">
                        <div className="tag-section-mobile-section">
                          <div className="tag-section">
                            <div className="timer-sk" style={{ width: "80px", height: "35px", borderRadius: "100px" }} />
                          </div>
                          <div className="billable-section">
                            <div className="timer-sk" style={{ width: "45px", height: "31px", borderRadius: "100px" }} />
                          </div>
                        </div>
                        <div className="overview-sec-timer-date-time">
                          <Col>
                            <div className="timer-sk" style={{ width: "140px", height: "20px" }} />
                          </Col>
                        </div>
                        <div className="overview-time-edit">
                          <Col>
                            <div className="overview-time-edit-inner">
                              <div className="timer-sk" style={{ width: "70px", height: "20px" }} />
                            </div>
                          </Col>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", gap: "12px" }}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" stroke="#E0E0E0" strokeWidth="2"/>
            <path d="M24 14v10l6 4" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p style={{ margin: 0, fontSize: "16px", fontWeight: "500", color: "#282828" }}>No time entries yet</p>
          <p style={{ margin: 0, fontSize: "13px", color: "#96A8BA", textAlign: "center" }}>Start tracking time using the timer above.</p>
        </div>
      )}

      <Modal
        isOpen={taskModalIsOpen}
        onRequestClose={() => setTaskModalIsOpen(false)}
      >
        <h5>Task</h5>
        <input
          type="text"
          placeholder="What are you working on?"
          value={taskName || ''}
          onChange={(e) => setTaskName(e.target.value)}
          autoComplete="off"
        />
        <button onClick={() => setTaskModalIsOpen(false)}>Close Modal</button>
      </Modal>
      <div className="pagination-outer">
        <div className="showing-count">
          {!isTimerDataLoading && timer.length > 0 ? (
            <div className="filter-inner showing-result">
              <label>Showing</label>
              <div
                ref={showingDropdownRef}
                className="select-modal-mainM custom-dropdown-user-new-outer"
              >
                <div
                  className="selected-option custom-dropdown-user-new"
                  onClick={() => setShowingDropdownOpen(!showingDropdownOpen)}
                >
                  {totalResults < 10 ? totalResults.toString() : perPage.toString()}
                </div>
                {showingDropdownOpen && (
                  <div className="custom-dropdown-new">
                    <ul className="showing-options-list">
                      {[10, 20, 30, 40].map((num) =>
                        totalResults >= num ? (
                          <li
                            key={num}
                            className={perPage === num ? "selected-item" : ""}
                            onClick={() => {
                              setPerPage(num);
                              setSelectedPage(0);
                              setShowingDropdownOpen(false);
                            }}
                          >
                            {num}
                          </li>
                        ) : null
                      )}

                      {totalResults >= 10 && (
                        <li
                          className={perPage === totalResults ? "selected-item" : ""}
                          onClick={() => {
                            setPerPage(totalResults);
                            setSelectedPage(0);
                            setShowingDropdownOpen(false);
                          }}
                        >
                          ALL
                        </li>
                      )}

                      {totalResults < 10 && (
                        <li
                          className="selected-item"
                          onClick={() => {
                            setPerPage(totalResults);
                            setSelectedPage(0);
                            setShowingDropdownOpen(false);
                          }}
                        >
                          {totalResults}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <label>of {totalResults} Results</label>
            </div>
          ) : null}

        </div>
        <div className="pagination" style={{ position: "relative", minHeight: "40px" }}>
          {pageCount > 1 && (
            <>
              {isTimerDataLoading ? (
                <ul className="pagination maininteampag pagination-loading">
                  {/* Skeleton for Back button - Only show if there would be a previous page */}
                  {selectedPage > 0 && (
                    <li className="previous">
                      <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                        &lt; Back
                      </a>
                    </li>
                  )}
                  {/* Skeleton for page numbers - Show first page, current page area, and last page */}
                  <li>
                    <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                      1
                    </a>
                  </li>
                  {pageCount > 2 && (
                    <>
                      {selectedPage > 1 && (
                        <li className="break-me">
                          <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                            ...
                          </a>
                        </li>
                      )}
                      {selectedPage > 0 && selectedPage < pageCount - 1 && (
                        <li className="active">
                          <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                            {selectedPage + 1}
                          </a>
                        </li>
                      )}
                      {selectedPage < pageCount - 2 && (
                        <li className="break-me">
                          <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                            ...
                          </a>
                        </li>
                      )}
                    </>
                  )}
                  {pageCount > 1 && (
                    <li>
                      <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                        {pageCount}
                      </a>
                    </li>
                  )}
                  {/* Skeleton for Next button - Only show if there would be a next page */}
                  {selectedPage < pageCount - 1 && (
                    <li className="next">
                      <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                        Next &gt;
                      </a>
                    </li>
                  )}
                </ul>
              ) : (
                <div
                  style={{
                    pointerEvents: isTimerDataLoading ? "none" : "auto",
                    opacity: isTimerDataLoading ? 0.5 : 1,
                    transition: "opacity 0.2s ease-in-out"
                  }}
                >
                  <ReactPaginate
                    previousLabel={selectedPage > 0 ? "< Back" : null}
                    nextLabel={selectedPage < pageCount - 1 ? "Next >" : null}
                    breakLabel={"..."}
                    breakClassName={"break-me"}
                    pageCount={pageCount}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={1}
                    onPageChange={handlePageChange}
                    containerClassName={`pagination maininteampag ${isTimerDataLoading ? "pagination-loading" : ""}`}
                    activeClassName={"active"}
                    forcePage={selectedPage}
                    disabledClassName={"disabled"}
                    disableInitialCallback={true}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default TimerList;